/**
 * Discogs Normalizer
 * Transforms Discogs API responses to frontend models for product editing.
 * @module api/normalizers/discogs
 */

import { safeString, safeNumber } from "../safeHelpers"
import { resolveCountryDictionaryItem } from "./country.normalizer"

// LLM:METADATA
// :hierarchy: [Domain:Discogs | Data_Normalization | Service]
// :relates-to: uses: "safeHelpers", required-by: "useModuleEditState.js discogsProductFill"
// :rationale: "Centralizes Discogs response normalization logic. Handles both legacy (tracklist/album/year_edition) and new (m_item_tracklist/m_item_title/m_item_year) field naming conventions from backend response. Provides country string mapping against dictionary items."
// :contract: pre: "raw Discogs response from backend", post: "normalized discogs data with consistent field names", invariant: "all fields are safely coerced with fallbacks"
// :decision_cache: "Created dedicated normalizer for Discogs data to separate normalization from component logic [FE-DISCOGS-NORM-001]"
// LLM:END

/**
 * Country alias mappings for common Discogs country strings
 * Maps variations to expected dictionary titles
 */
/**
 * Resolve country string against dictionary items
 * Performs case-insensitive matching with alias support
 *
 * @param {string} countryString - Raw country string from Discogs
 * @param {Array} countryDict - Country dictionary items with id/title structure
 * @returns {Object|null} Matched country dict item or null if not found
 */
export function resolveCountryFromDict(countryString, countryDict) {
  return resolveCountryDictionaryItem(countryString, countryDict)
}

/**
 * Normalize artist data from Discogs response
 * Handles multiple shapes: object with m_item_id/m_item_title, object with id/title, or string
 *
 * @param {Object|string} artist - Raw artist data
 * @returns {Object} Normalized artist object with id/title or error string
 */
function normalizeArtist(artist) {
  if (artist?.m_item_id != undefined) {
    // New format: {m_item_id, m_item_title}
    return {
      id: safeNumber(artist.m_item_id),
      title: safeString(artist.m_item_title),
      found: true,
    }
  }

  if (artist?.id != undefined && artist?.title != undefined) {
    // Old format: {id, title}
    return {
      id: safeNumber(artist.id),
      title: safeString(artist.title),
      found: true,
    }
  }

  if (typeof artist === "string" && artist !== "") {
    // Error case: string message
    return {
      found: false,
      message: safeString(artist),
    }
  }

  return { found: false }
}

/**
 * Normalize country data from Discogs response
 * Handles multiple shapes: object with m_item_id/m_item_title, object with id/title, or string
 * Also performs dictionary-based string matching for raw country strings
 *
 * @param {Object|string} country - Raw country data
 * @param {Array} [countryDict] - Optional country dictionary for string resolution
 * @returns {Object} Normalized country object with id/title or error info
 */
function normalizeCountry(country, countryDict) {
  if (country?.m_item_id != undefined) {
    // New format: {m_item_id, m_item_title}
    return {
      id: safeNumber(country.m_item_id),
      title: safeString(country.m_item_title),
      found: true,
      source: "direct",
    }
  }

  if (country?.id != undefined && country?.title != undefined) {
    // Old format: {id, title}
    return {
      id: safeNumber(country.id),
      title: safeString(country.title),
      found: true,
      source: "direct",
    }
  }

  if (typeof country === "string" && country !== "") {
    // String case - try to resolve against dictionary
    if (countryDict) {
      const resolved = resolveCountryFromDict(country, countryDict)
      if (resolved) {
        return {
          id: safeNumber(resolved.id),
          title: safeString(resolved.title),
          found: true,
          source: "resolved",
          originalString: safeString(country),
        }
      }
    }

    // Error case: string message or unresolved country
    return {
      found: false,
      message: safeString(country),
      source: countryDict ? "unresolved" : "string",
    }
  }

  return { found: false }
}

/**
 * Normalize label data from Discogs response
 * Handles multiple shapes: object with m_item_id/m_item_title, object with id/title, or string
 *
 * @param {Object|string} label - Raw label data
 * @returns {Object} Normalized label object with id/title or error string
 */
function normalizeLabel(label) {
  if (label?.m_item_id != undefined) {
    // New format: {m_item_id, m_item_title}
    return {
      id: safeNumber(label.m_item_id),
      title: safeString(label.m_item_title),
      found: true,
    }
  }

  if (label?.id != undefined && label?.title != undefined) {
    // Old format: {id, title}
    return {
      id: safeNumber(label.id),
      title: safeString(label.title),
      found: true,
    }
  }

  if (typeof label === "string" && label !== "") {
    // Error case: string message
    return {
      found: false,
      message: safeString(label),
    }
  }

  return { found: false }
}

/**
 * Normalize Discogs product data response
 * Transforms raw backend Discogs response to normalized structure
 *
 * @param {Object|string} result - Raw Discogs response object or string
 * @param {Object} options - Normalization options
 * @param {string} [options.moduleName] - Module name for year field mapping (lp vs others)
 * @param {Array} [options.countryDict] - Country dictionary for string resolution
 * @returns {Object} Normalized Discogs data
 * @returns {string} [returns.tracklist] - Normalized tracklist
 * @returns {string} [returns.year] - Normalized year
 * @returns {string} [returns.title] - Normalized title
 * @returns {Object} [returns.artist] - Normalized artist data
 * @returns {Object} [returns.label] - Normalized label data
 * @returns {Object} [returns.country] - Normalized country data
 * @returns {boolean} returns.isString - True if input was a string (legacy case)
 */
export function normalizeDiscogsResponse(result, options = {}) {
  // eslint-disable-next-line no-unused-vars
  const { moduleName = "", countryDict } = options

  // Handle string-only response (legacy case)
  if (typeof result === "string") {
    return {
      tracklist: result,
      isString: true,
    }
  }

  if (!result || typeof result !== "object") {
    return {
      isString: false,
    }
  }

  // LLM:METADATA
  // :topology: [Domain:Discogs | Normalization | Field_Fallback]
  // :relates-to: uses: "result.tracklist, result.m_item_tracklist"
  // :rationale: "Support both old (tracklist) and new (m_item_tracklist) field names from backend response."
  // :contract: pre: "result provided", post: "tracklist field populated if value exists", invariant: "only first matching value is used"
  // :decision_cache: "Check both naming conventions for backward compatibility [FE-DISCOGS-COMPAT-001]"
  // LLM:END
  const tracklistValue = result.tracklist || result.m_item_tracklist

  // LLM:METADATA
  // :topology: [Domain:Discogs | Normalization | Year_Field_Fallback]
  // :relates-to: uses: "result.year_edition, result.m_item_year, moduleName"
  // :rationale: "Support both old (year_edition) and new (m_item_year) field names. Map to appropriate field based on module type."
  // :contract: pre: "result and moduleName provided", post: "yearValue extracted based on module type", invariant: "lp uses year_edition, others use year"
  // :decision_cache: "Check both naming conventions for backward compatibility [FE-DISCOGS-COMPAT-001]"
  // LLM:END
  const yearValue = result.year_edition || result.m_item_year

  // LLM:METADATA
  // :topology: [Domain:Discogs | Normalization | Title_Field_Fallback]
  // :relates-to: uses: "result.album, result.m_item_title"
  // :rationale: "Support both old (album) and new (m_item_title) field names for the title field."
  // :contract: pre: "result provided", post: "titleValue extracted if value exists", invariant: "only first matching value is used"
  // :decision_cache: "Check both naming conventions for backward compatibility [FE-DISCOGS-COMPAT-001]"
  // LLM:END
  const titleValue = result.album || result.m_item_title

  return {
    tracklist: safeString(tracklistValue),
    year: safeString(yearValue),
    title: safeString(titleValue),
    artist: normalizeArtist(result.artist),
    country: normalizeCountry(
      result.country || result.m_item_country,
      countryDict
    ),
    label: normalizeLabel(result.label),
    isString: false,
    // Preserve original values for debugging
    raw: {
      tracklist: tracklistValue,
      year: yearValue,
      title: titleValue,
      artist: result.artist,
      country: result.country || result.m_item_country,
      label: result.label,
    },
  }
}

/**
 * Check if normalized artist was successfully found
 *
 * @param {Object} normalizedArtist - Normalized artist object
 * @returns {boolean} True if artist was found
 */
export function isArtistFound(normalizedArtist) {
  return normalizedArtist?.found === true
}

/**
 * Check if normalized country was successfully found
 *
 * @param {Object} normalizedCountry - Normalized country object
 * @returns {boolean} True if country was found
 */
export function isCountryFound(normalizedCountry) {
  return normalizedCountry?.found === true
}

/**
 * Check if normalized label was successfully found
 *
 * @param {Object} normalizedLabel - Normalized label object
 * @returns {boolean} True if label was found
 */
export function isLabelFound(normalizedLabel) {
  return normalizedLabel?.found === true
}
